'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_DEFAULT_PIXELS_PER_FRAME, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MOUSE_PIXELS_PER_FRAME, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_TOUCH_PIXELS_PER_FRAME } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/constants';
import type { SpartanProductExteriorThreesixtyRotationDirection } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/types';
import { scaleThreesixtyPixelsPerFrame } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/utils';
const TOUCH_DIRECTION_LOCK_THRESHOLD_PX = 8;
const FOLLOW_FACTOR_AT_60_HZ = 0.15;
const FRAME_DURATION_MS = 1000 / 60;
const FOLLOW_STOP_DISTANCE_PX = 0.5;
const RELEASE_FOLLOW_MAX_DURATION_MS = 140;
const getRotationDirection = (delta: number): SpartanProductExteriorThreesixtyRotationDirection => {
  if (delta === 0) return 0;
  return delta > 0 ? 1 : -1;
};
const getPixelsPerFrame = (pointerType: string, frameCount: number) => {
  const basePixelsPerFrame = pointerType === 'touch' ? SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_TOUCH_PIXELS_PER_FRAME : pointerType === 'mouse' ? SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MOUSE_PIXELS_PER_FRAME : SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_DEFAULT_PIXELS_PER_FRAME;
  const viewportPixelsPerFrame = frameCount > 0 ? window.innerWidth / frameCount : basePixelsPerFrame;
  return Math.max(scaleThreesixtyPixelsPerFrame(basePixelsPerFrame, frameCount), viewportPixelsPerFrame);
};
interface UseInlineThreesixtyRotationOptions {
  reducedMotion?: boolean;
  getDisplayedIndex?: () => number | null;
  onDirectionChange?: (direction: Exclude<SpartanProductExteriorThreesixtyRotationDirection, 0>) => void;
  onIndexChange?: (index: number, direction: SpartanProductExteriorThreesixtyRotationDirection) => void;
  onInteractionStart?: () => void;
  onInteractionEnd?: (index: number) => void;
  onDragEnd?: (distanceInFrames: number, pointerType: string) => void;
}

/**
 * Inline-only rotation controller. Touch input is direction locked so a
 * vertical gesture remains available to scroll the document.
 */
export const useInlineThreesixtyRotation = (frameCount: number, enabled: boolean, options: UseInlineThreesixtyRotationOptions = {}) => {
  const {
    getDisplayedIndex,
    onDirectionChange,
    onIndexChange,
    onInteractionStart,
    onInteractionEnd,
    reducedMotion = false
  } = options;
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const isDraggingRef = useRef(false);
  const activePointerIdRef = useRef<number | null>(null);
  const pointerCaptureTargetRef = useRef<Element | null>(null);
  const pointerTypeRef = useRef('mouse');
  const draggedDistanceRef = useRef(0);
  const pointerStartRef = useRef({
    x: 0,
    y: 0
  });
  const isTouchDirectionResolvedRef = useRef(false);
  const animationFrameRef = useRef<number | null>(null);
  const lastAnimationTimeRef = useRef<number | null>(null);
  const releaseFollowRef = useRef<{
    startPosition: number;
    startedAt: number;
    durationMs: number;
  } | null>(null);
  const lastXRef = useRef(0);
  const targetPositionRef = useRef(0);
  const displayedPositionRef = useRef(0);
  const hasPendingInteractionRef = useRef(false);
  const pixelsPerFrameRef = useRef(scaleThreesixtyPixelsPerFrame(SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_DEFAULT_PIXELS_PER_FRAME, frameCount));
  const renderedIndexRef = useRef(0);
  const pointerDirectionRef = useRef<SpartanProductExteriorThreesixtyRotationDirection>(0);
  const pendingDirectionRef = useRef<SpartanProductExteriorThreesixtyRotationDirection>(0);
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);
  const wrapIndex = useCallback((value: number) => {
    if (frameCount <= 0) return 0;
    const next = value % frameCount;
    return next < 0 ? next + frameCount : next;
  }, [frameCount]);
  const cancelScheduledDraw = useCallback(() => {
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    lastAnimationTimeRef.current = null;
    releaseFollowRef.current = null;
  }, []);
  const drawDisplayedPosition = useCallback(() => {
    const frames = Math.trunc(displayedPositionRef.current / pixelsPerFrameRef.current);
    const nextIndex = wrapIndex(frames);
    if (nextIndex === renderedIndexRef.current) return;
    renderedIndexRef.current = nextIndex;
    optionsRef.current.onIndexChange?.(nextIndex, pendingDirectionRef.current);
  }, [wrapIndex]);
  const completeInteraction = useCallback(() => {
    if (!hasPendingInteractionRef.current) return;
    hasPendingInteractionRef.current = false;
    pointerDirectionRef.current = 0;
    pendingDirectionRef.current = 0;
    optionsRef.current.onInteractionEnd?.(renderedIndexRef.current);
  }, []);
  const advancePosition = useCallback(function followTarget(timestamp: number) {
    animationFrameRef.current = null;
    const releaseFollow = releaseFollowRef.current;
    if (releaseFollow) {
      // Evenly spaced frames avoid a long pause before the final image.
      const progress = Math.min(1, Math.max(0, (timestamp - releaseFollow.startedAt) / releaseFollow.durationMs));
      if (progress === 1) {
        displayedPositionRef.current = targetPositionRef.current;
        releaseFollowRef.current = null;
      } else {
        displayedPositionRef.current = releaseFollow.startPosition + (targetPositionRef.current - releaseFollow.startPosition) * progress;
      }
    } else {
      const elapsed = Math.max(0, timestamp - (lastAnimationTimeRef.current ?? timestamp - FRAME_DURATION_MS));
      lastAnimationTimeRef.current = timestamp;
      // Keep the same follow rate on 60 Hz and high-refresh displays.
      const followFactor = 1 - Math.pow(1 - FOLLOW_FACTOR_AT_60_HZ, elapsed / FRAME_DURATION_MS);
      displayedPositionRef.current += (targetPositionRef.current - displayedPositionRef.current) * followFactor;
    }
    if (Math.abs(targetPositionRef.current - displayedPositionRef.current) <= FOLLOW_STOP_DISTANCE_PX) {
      displayedPositionRef.current = targetPositionRef.current;
    }
    drawDisplayedPosition();
    if (displayedPositionRef.current !== targetPositionRef.current) {
      animationFrameRef.current = window.requestAnimationFrame(followTarget);
    } else {
      lastAnimationTimeRef.current = null;
      if (!isDraggingRef.current) completeInteraction();
    }
  }, [completeInteraction, drawDisplayedPosition]);
  const scheduleDraw = useCallback(() => {
    if (animationFrameRef.current !== null) return;
    lastAnimationTimeRef.current = performance.now();
    animationFrameRef.current = window.requestAnimationFrame(advancePosition);
  }, [advancePosition]);
  const releasePointerCapture = useCallback((pointerId: number) => {
    const target = pointerCaptureTargetRef.current;
    pointerCaptureTargetRef.current = null;
    if (!target) return;
    try {
      if (target.hasPointerCapture(pointerId)) {
        target.releasePointerCapture(pointerId);
      }
    } catch {
      // WebKit can release capture before pointercancel/lostpointercapture.
    }
  }, []);
  const beginDragging = useCallback((clientX: number, pointerType: string) => {
    cancelScheduledDraw();
    const displayedIndex = optionsRef.current.getDisplayedIndex?.() ?? renderedIndexRef.current;
    pixelsPerFrameRef.current = getPixelsPerFrame(pointerType, frameCount);
    draggedDistanceRef.current = 0;
    renderedIndexRef.current = displayedIndex;
    targetPositionRef.current = displayedIndex * pixelsPerFrameRef.current;
    displayedPositionRef.current = targetPositionRef.current;
    lastXRef.current = clientX;
    pointerDirectionRef.current = 0;
    pendingDirectionRef.current = 0;
    hasPendingInteractionRef.current = true;
    isDraggingRef.current = true;
    setIsDragging(true);
    optionsRef.current.onInteractionStart?.();
  }, [cancelScheduledDraw, frameCount]);
  const moveDragging = useCallback((clientX_0: number) => {
    if (!isDraggingRef.current) return;
    const delta = -(clientX_0 - lastXRef.current);
    lastXRef.current = clientX_0;
    if (delta === 0) return;
    draggedDistanceRef.current += Math.abs(delta);
    const direction: Exclude<SpartanProductExteriorThreesixtyRotationDirection, 0> = delta > 0 ? 1 : -1;
    if (direction !== pointerDirectionRef.current) {
      pointerDirectionRef.current = direction;
      optionsRef.current.onDirectionChange?.(direction);
    }
    pendingDirectionRef.current = direction;
    targetPositionRef.current += delta;
    if (optionsRef.current.reducedMotion) {
      displayedPositionRef.current = targetPositionRef.current;
      drawDisplayedPosition();
    } else {
      scheduleDraw();
    }
  }, [drawDisplayedPosition, scheduleDraw]);
  const finishDragging = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
    optionsRef.current.onDragEnd?.(draggedDistanceRef.current / pixelsPerFrameRef.current, pointerTypeRef.current);
    const remainingFrames = Math.abs(Math.trunc(targetPositionRef.current / pixelsPerFrameRef.current) - Math.trunc(displayedPositionRef.current / pixelsPerFrameRef.current));
    if (optionsRef.current.reducedMotion || remainingFrames <= 1 || Math.abs(targetPositionRef.current - displayedPositionRef.current) <= FOLLOW_STOP_DISTANCE_PX) {
      cancelScheduledDraw();
      displayedPositionRef.current = targetPositionRef.current;
      drawDisplayedPosition();
      completeInteraction();
      return;
    }
    releaseFollowRef.current = {
      startPosition: displayedPositionRef.current,
      startedAt: performance.now(),
      durationMs: Math.min(RELEASE_FOLLOW_MAX_DURATION_MS, remainingFrames * FRAME_DURATION_MS)
    };
    scheduleDraw();
  }, [cancelScheduledDraw, completeInteraction, drawDisplayedPosition, scheduleDraw]);
  const cancelDragging = useCallback(() => {
    if (!isDraggingRef.current) return;
    cancelScheduledDraw();
    isDraggingRef.current = false;
    setIsDragging(false);
    targetPositionRef.current = renderedIndexRef.current * pixelsPerFrameRef.current;
    displayedPositionRef.current = targetPositionRef.current;
    completeInteraction();
  }, [cancelScheduledDraw, completeInteraction]);
  const clearPointer = useCallback((shouldReleaseCapture = true) => {
    const pointerId_0 = activePointerIdRef.current;
    activePointerIdRef.current = null;
    if (pointerId_0 !== null && shouldReleaseCapture) {
      releasePointerCapture(pointerId_0);
    } else {
      pointerCaptureTargetRef.current = null;
    }
    isTouchDirectionResolvedRef.current = false;
  }, [releasePointerCapture]);
  const stop = useCallback(() => {
    cancelScheduledDraw();
    clearPointer();
    hasPendingInteractionRef.current = false;
    isDraggingRef.current = false;
    pointerDirectionRef.current = 0;
    pendingDirectionRef.current = 0;
    targetPositionRef.current = renderedIndexRef.current * pixelsPerFrameRef.current;
    displayedPositionRef.current = targetPositionRef.current;
    setIsDragging(false);
  }, [cancelScheduledDraw, clearPointer]);
  const setIndex = useCallback((index: number, direction_0: SpartanProductExteriorThreesixtyRotationDirection = 0) => {
    const nextIndex_0 = wrapIndex(index);
    stop();
    targetPositionRef.current = nextIndex_0 * pixelsPerFrameRef.current;
    displayedPositionRef.current = targetPositionRef.current;
    renderedIndexRef.current = nextIndex_0;
    optionsRef.current.onIndexChange?.(nextIndex_0, direction_0);
  }, [stop, wrapIndex]);
  const step = useCallback((deltaFrames: number) => {
    if (!enabled || frameCount <= 0 || deltaFrames === 0) return;
    const nextIndex_1 = wrapIndex(renderedIndexRef.current + deltaFrames);
    setIndex(nextIndex_1, getRotationDirection(deltaFrames));
    optionsRef.current.onInteractionEnd?.(nextIndex_1);
  }, [enabled, frameCount, setIndex, wrapIndex]);
  const capturePointer = (event: React.PointerEvent<HTMLElement>) => {
    const target_0 = event.currentTarget;
    pointerCaptureTargetRef.current = target_0;
    try {
      target_0.setPointerCapture(event.pointerId);
    } catch {
      pointerCaptureTargetRef.current = null;
    }
  };
  const handlePointerDown = (event_0: React.PointerEvent<HTMLElement>) => {
    if (!enabled || event_0.button !== 0 || !event_0.isPrimary || activePointerIdRef.current !== null) {
      return;
    }
    activePointerIdRef.current = event_0.pointerId;
    pointerTypeRef.current = event_0.pointerType;
    pointerStartRef.current = {
      x: event_0.clientX,
      y: event_0.clientY
    };
    isTouchDirectionResolvedRef.current = event_0.pointerType !== 'touch';
    if (event_0.pointerType !== 'touch') {
      capturePointer(event_0);
      beginDragging(event_0.clientX, event_0.pointerType);
    }
  };
  const handlePointerMove = (event_1: React.PointerEvent<HTMLElement>) => {
    if (event_1.pointerId !== activePointerIdRef.current) return;
    if (pointerTypeRef.current === 'touch' && !isTouchDirectionResolvedRef.current) {
      const deltaX = event_1.clientX - pointerStartRef.current.x;
      const deltaY = event_1.clientY - pointerStartRef.current.y;
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < TOUCH_DIRECTION_LOCK_THRESHOLD_PX) {
        return;
      }
      isTouchDirectionResolvedRef.current = true;
      if (Math.abs(deltaY) >= Math.abs(deltaX)) {
        clearPointer(false);
        return;
      }
      capturePointer(event_1);
      beginDragging(pointerStartRef.current.x, 'touch');
    }
    if (!isDraggingRef.current) return;
    const coalescedEvents = event_1.nativeEvent.getCoalescedEvents?.() ?? [];
    const latestEvent = coalescedEvents.at(-1) ?? event_1.nativeEvent;
    moveDragging(latestEvent.clientX);
  };
  const handlePointerUp = (event_2: React.PointerEvent<HTMLElement>) => {
    if (event_2.pointerId !== activePointerIdRef.current) return;
    finishDragging();
    clearPointer();
  };
  const handlePointerCancel = (event_3: React.PointerEvent<HTMLElement>) => {
    if (event_3.pointerId !== activePointerIdRef.current) return;
    cancelDragging();
    clearPointer(false);
  };
  const handleLostPointerCapture = (event_4: React.PointerEvent<HTMLElement>) => {
    if (event_4.pointerId !== activePointerIdRef.current) return;
    cancelDragging();
    clearPointer(false);
  };
  const handleKeyDown = (event_5: React.KeyboardEvent<HTMLElement>) => {
    if (!enabled) return;
    if (event_5.key === 'ArrowLeft') {
      event_5.preventDefault();
      step(-1);
    } else if (event_5.key === 'ArrowRight') {
      event_5.preventDefault();
      step(1);
    }
  };
  useEffect(() => {
    stop();
    renderedIndexRef.current = 0;
    targetPositionRef.current = 0;
    displayedPositionRef.current = 0;
    pixelsPerFrameRef.current = scaleThreesixtyPixelsPerFrame(SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_DEFAULT_PIXELS_PER_FRAME, frameCount);
    optionsRef.current.onIndexChange?.(0, 0);
    return stop;
  }, [enabled, frameCount, stop]);
  useEffect(() => {
    if (!reducedMotion || animationFrameRef.current === null) return;
    cancelScheduledDraw();
    displayedPositionRef.current = targetPositionRef.current;
    drawDisplayedPosition();
    if (!isDraggingRef.current) completeInteraction();
  }, [cancelScheduledDraw, completeInteraction, drawDisplayedPosition, reducedMotion]);
  return {
    isDragging,
    handleKeyDown,
    handleLostPointerCapture,
    handlePointerCancel,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    setIndex,
    stop
  };
};